import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import loadable from '@loadable/component';

import './emails.css';
import { removeBlock, duplicateBlock } from '../../../utils/emailBlocks';
import {
  CANVAS_DROPPABLE, withBlocks, labelFor, blockSummary, summaryOf,
  previewOf, canvasStyle, inlineFieldFor,
} from '../../../utils/emailBlockDoc';
import { isContentSafeForRichText } from '../../../utils/tiptapConfig';
import { imageSrcKind } from '../../../utils/emailImageSrc';

// THE EMAIL IS THE EDITOR.
//
// This is the right-hand pane, and it is the email: real fonts, real colours,
// the card on its own background, the button drawn as the button. You drop a
// block into it from the palette on the left, click a block in it to select it,
// and type into it where there is text to type. There is no second picture of
// the email anywhere on the screen and no panel far from the thing it affects.
//
// Two rejected layouts got here. The first drew a faithful miniature on the left
// and a proof on the right — the same email twice, disagreeing. The second made
// the left column a structural list of rows, which stopped it being a second
// picture but also stopped it being the email, so editing still happened
// somewhere other than where you were looking. The instruction after that was
// explicit: one surface, showing the email, worked on directly.
//
// WHY THIS IS NOT THE IFRAME. The proof pane renders the generated HTML inside
// `<iframe sandbox="" srcDoc=...>`, and `sandbox=""` applies every restriction
// at once — which is exactly right for showing untrusted template HTML and
// exactly wrong for editing: you cannot drop into it, click into it or type
// inside it. So the editable surface is real DOM, and the iframe stays, whole
// and unchanged, as a mode of its own on this same pane's device control. It is
// still the only thing that shows what the server will actually send.
//
// WHAT KEEPS THE TWO IN AGREEMENT. Every number here comes from `previewOf` and
// `canvasStyle` (src/utils/emailBlockDoc.js), which share `clamp`, `align` and
// `GUTTER` with the serialiser rather than restating them, and the tests render
// the same block through `previewOf` and through `renderBlocks` and assert the
// numbers agree. Nothing in this file does arithmetic on a block's props.
//
// DRAG. The DragDropContext is one level up, in EmailTemplateEditor, because the
// palette and the canvas are now in different columns of the `.press` grid and a
// tile has to travel between them. The shape is unchanged from what worked:
// `isDropDisabled` source Droppables in the palette, this one destination
// Droppable, one `onDragEnd` switching on `source.droppableId` inside
// `applyDragEnd`. react-beautiful-dnd 13 needs string `draggableId`s (block ids
// already are) and does not survive React 18 StrictMode — src/browser/index.jsx
// deliberately does not mount StrictMode; if that changes, this breaks first and
// silently, by simply not accepting drops.

// ProseMirror needs a DOM and /cms/:tab is server-rendered, so the editor stays
// out of the server bundle — ssr:false, exactly as EmailCellEditor loads it.
const RichTextEditor = loadable(() => import('./RichTextEditor'), {
  ssr: false,
  fallback: <div className="emails-rte-loading">Loading editor…</div>,
});

const GRIP = (
  <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor" aria-hidden="true">
    <circle cx="2.2" cy="2.2" r="1.2" />
    <circle cx="7.8" cy="2.2" r="1.2" />
    <circle cx="2.2" cy="7" r="1.2" />
    <circle cx="7.8" cy="7" r="1.2" />
    <circle cx="2.2" cy="11.8" r="1.2" />
    <circle cx="7.8" cy="11.8" r="1.2" />
  </svg>
);

/*
One line of plain text, edited where it is drawn.

A heading and a button label are single strings, so they are `contentEditable`
rather than an editor: the styling that makes them look like the email is the
same style whether you are typing or not, which is the entire point of editing
in place.

It is UNCONTROLLED on purpose, and the guard is the same one RichTextEditor
needs and for the same reason. React children would rewrite the node on every
keystroke — the keystroke lifts state, the parent re-renders, the new text is
written back and the caret jumps to the start, which reads as "I press keys and
nothing happens". So this renders no children at all; the effect below writes
`textContent`, and only ever while the node is not the focused one.
*/
const InlineText = ({ tag, value, editable, onChange, style, className, placeholder }) => {
  const ref = useRef(null);
  const Tag = tag;

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    // Never while the caret is in it. See above.
    if (typeof document !== 'undefined' && document.activeElement === node) return;
    if (node.textContent !== value) node.textContent = value;
  }, [value, editable]);

  return (
    <Tag
      ref={ref}
      className={className}
      style={style}
      data-placeholder={placeholder}
      contentEditable={editable}
      suppressContentEditableWarning
      role={editable ? 'textbox' : undefined}
      tabIndex={editable ? 0 : undefined}
      onInput={event => onChange(event.currentTarget.textContent)}
      // One line means one line. Enter would put a <br> or a <div> into what the
      // serialiser escapes as text, so it commits and leaves instead.
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.currentTarget.blur();
        }
      }}
      // A paste out of a browser or a document carries markup; `textContent` is
      // read back from this node, so the markup would arrive as literal escaped
      // tags in the email. Take the text and nothing else.
      onPaste={(event) => {
        const text = event.clipboardData && event.clipboardData.getData('text/plain');
        if (text === undefined || text === null) return;
        event.preventDefault();
        // eslint-disable-next-line no-undef
        document.execCommand('insertText', false, text.replace(/\s*\n\s*/g, ' '));
      }}
    />
  );
};

InlineText.propTypes = {
  tag: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  editable: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  style: PropTypes.shape({}).isRequired,
  className: PropTypes.string,
  placeholder: PropTypes.string,
};

InlineText.defaultProps = { className: undefined, placeholder: '' };

// The block, drawn as the email draws it. `preview` is the only source of every
// size, colour and pad here — see the note at the top about why.
const BlockBody = ({ block, preview, isSelected, onPatch }) => {
  switch (preview.kind) {
    case 'heading':
      return (
        <InlineText
          tag={`h${preview.level}`}
          className="emailblock-inline"
          value={preview.text}
          editable={isSelected}
          placeholder="Your heading"
          style={preview.style}
          onChange={text => onPatch({ text })}
        />
      );

    case 'text':
      // Tiptap in the block itself when it is selected, the rendered paragraph
      // when it is not. showButtonControls is false: in a block document a
      // button is its OWN block, and one inserted into this rich text would
      // render inside this block's cell instead — see the long comment on the
      // controls in RichTextEditor.jsx, both mechanisms are needed.
      //
      // isContentSafeForRichText is the second condition and it is not optional.
      // The Tiptap schema is a whitelist and drops everything it does not model
      // on the first save, so a block holding such markup must NOT take the
      // caret here; it renders, and the properties panel edits it as HTML.
      //
      // TWO STRINGS, ON PURPOSE. `preview.editHtml` is what is STORED and is
      // what Tiptap edits — it writes back what it was handed on the next
      // keystroke, so handing it anything else would rewrite the document.
      // `preview.html` is the same string with the declarations the block's own
      // properties override taken out of it, which is exactly what the email
      // sends. `preview.overrideClass` lets emails.css neutralise, for display
      // only, the declarations still on the nodes inside the live editor.
      return isSelected && isContentSafeForRichText(preview.editHtml) ? (
        <div
          className={['emailblock-rte', preview.overrideClass].filter(Boolean).join(' ')}
          style={preview.style}
        >
          <RichTextEditor
            key={block.id}
            value={preview.editHtml}
            onChange={html => onPatch({ html })}
            showButtonControls={false}
          />
        </div>
      ) : (
        <div
          style={preview.style}
          className={preview.html ? undefined : 'emailblock-placeholder'}
          /* The block's own rich text, emitted verbatim — the serialiser emits
             the same string the same way (see the DELIBERATE EXCEPTION comment
             on renderText), and escaping it here would show tags where the
             email shows formatting.

             This is the one place in this admin that puts stored markup into
             the CMS's own document rather than into the proof's `sandbox=""`
             iframe, so it is worth being plain about what that means: React
             does not run a <script> inserted this way, but an inline handler
             on an element would run in the CMS's origin. The corpus is
             admin-authored and this same string already reaches the inbox
             unescaped, so it is not a new trust boundary — but it is a real
             one, and a sanitiser here is the obvious next thing if these
             documents ever come from anywhere but this screen. */
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: preview.html || 'Write something here.' }}
        />
      );

    case 'button':
      return (
        <InlineText
          tag="span"
          className="emailblock-inline"
          value={preview.label}
          editable={isSelected}
          placeholder="Button"
          style={preview.style}
          onChange={label => onPatch({ label })}
        />
      );

    case 'image': {
      // Three states, because a src holding `{{{imageUrl}}}` is neither a
      // picture nor nothing. The 26 converted PRO templates have exactly that
      // hero, and drawing an <img> at it gives a broken-image icon: an error
      // the editor did not make, on a template that is working perfectly.
      const srcKind = imageSrcKind(preview.src);
      if (srcKind === 'url') return <img src={preview.src} alt={preview.alt} style={preview.style} />;
      if (srcKind === 'variable') {
        return (
          <div className="emailblock-noimage">
            {`${preview.src.trim()} — the campaign supplies this image when the email is sent.`}
          </div>
        );
      }
      return <div className="emailblock-noimage">No image yet — choose one in Block properties.</div>;
    }

    case 'social':
      // A real <table><tr> of real <td>s, not a flex row: the serialiser emits
      // a table because Outlook lays inline-block out as a stack, and a canvas
      // drawn with flexbox would look right here and wrong in the inbox.
      return preview.icons.length === 0 ? (
        <div className="emailblock-noimage">
          No icons in this row yet — add one in Block properties. It is sent as nothing.
        </div>
      ) : (
        <table style={preview.style} className="emailblock-social">
          <tbody>
            <tr>
              {preview.icons.map(icon => (
                <td key={icon.src} style={icon.cellStyle}>
                  <span style={icon.chipStyle} className="emailblock-socialchip">
                    <img src={icon.src} alt={icon.alt} style={icon.style} />
                  </span>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      );

    case 'divider':
      return <div style={preview.style} className="emailblock-rule" />;

    case 'spacer':
      return <div style={preview.style} className="emailblock-space" />;

    default:
      return (
        <div className="emailblock-noimage">
          {`This build does not know a “${preview.type}” block. It is kept as it is and sent as nothing.`}
        </div>
      );
  }
};

BlockBody.propTypes = {
  block: PropTypes.shape({ id: PropTypes.string }).isRequired,
  preview: PropTypes.shape({
    kind: PropTypes.string.isRequired,
    type: PropTypes.string,
    level: PropTypes.number,
    text: PropTypes.string,
    html: PropTypes.string,
    editHtml: PropTypes.string,
    overrideClass: PropTypes.string,
    label: PropTypes.string,
    src: PropTypes.string,
    alt: PropTypes.string,
    icons: PropTypes.arrayOf(PropTypes.shape({ src: PropTypes.string })),
    style: PropTypes.shape({}).isRequired,
  }).isRequired,
  isSelected: PropTypes.bool.isRequired,
  onPatch: PropTypes.func.isRequired,
};

const BlockCanvas = ({ doc, selectedId, onChange, onSelect, onPatchBlock }) => {
  const { blocks } = doc;
  const canvas = canvasStyle(doc.settings);

  const commit = (nextBlocks, nextSelectedId) => {
    onChange(withBlocks(doc, nextBlocks));
    if (nextSelectedId !== undefined) onSelect(nextSelectedId);
  };

  const handleDuplicate = (block) => {
    const next = duplicateBlock(blocks, block.id);
    const index = next.findIndex(entry => entry.id === block.id);
    commit(next, index === -1 ? undefined : next[index + 1].id);
  };

  // Deleting asks first, always. Losing a block someone wrote is the failure
  // this whole feature exists to avoid, and there is no undo on the canvas —
  // the only safety net below this is the saved revision history.
  const handleDelete = (block) => {
    const summary = blockSummary(block);
    const what = summary ? `the ${labelFor(block.type).toLowerCase()} “${summary}”` : `this ${labelFor(block.type).toLowerCase()}`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${what}? This cannot be undone from here.`)) return;
    commit(removeBlock(blocks, block.id), selectedId === block.id ? null : undefined);
  };

  return (
    <div className="emailcanvas" style={canvas.page}>
      <Droppable droppableId={CANVAS_DROPPABLE}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={snapshot.isDraggingOver ? 'emailcanvas-card is-over' : 'emailcanvas-card'}
            style={canvas.card}
          >
            {blocks.length === 0 && (
              <div className="emailcanvas-empty">
                <b>This email has nothing in it yet.</b>
                <span>Drag a block in from the left, or click one to add it here.</span>
              </div>
            )}

            {blocks.map((block, index) => {
              const preview = previewOf(block, doc.settings);
              const isSelected = selectedId === block.id;
              const summary = summaryOf(block);
              return (
                <Draggable key={block.id} draggableId={block.id} index={index}>
                  {(dragProvided, dragSnapshot) => (
                    <div
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      className={[
                        'emailblock',
                        isSelected ? 'is-selected' : '',
                        dragSnapshot.isDragging ? 'is-dragging' : '',
                        inlineFieldFor(block) ? 'is-typable' : '',
                      ].filter(Boolean).join(' ')}
                      onClick={() => onSelect(block.id)}
                    >
                      <div className="emailblock-cell" style={preview.cellStyle}>
                        <BlockBody
                          block={block}
                          preview={preview}
                          isSelected={isSelected}
                          onPatch={patch => onPatchBlock(block.id, patch)}
                        />
                      </div>

                      {/* On hover or selection only. Left sitting on the email
                          permanently, they stop it looking like an email. */}
                      <div className="emailblock-tools">
                        <span
                          className="emailblock-grip"
                          {...dragProvided.dragHandleProps}
                          title={`Drag to move this ${labelFor(block.type).toLowerCase()}`}
                        >
                          {GRIP}
                        </span>
                        <span className="emailblock-label" title={summary.text}>{labelFor(block.type)}</span>
                        <button
                          type="button"
                          className="tbtn"
                          title="Duplicate this block"
                          onClick={(event) => { event.stopPropagation(); handleDuplicate(block); }}
                        >
                          Duplicate
                        </button>
                        <button
                          type="button"
                          className="tbtn danger"
                          title="Delete this block"
                          onClick={(event) => { event.stopPropagation(); handleDelete(block); }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
};

BlockCanvas.propTypes = {
  doc: PropTypes.shape({
    blocks: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })).isRequired,
    settings: PropTypes.shape({}).isRequired,
  }).isRequired,
  selectedId: PropTypes.string,
  // Takes a whole document — reorder, duplicate and delete all replace the list.
  onChange: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  // Takes (id, patch) — one prop of one block, which is what typing in place
  // produces on every keystroke. Kept apart from onChange so a keystroke does
  // not rebuild the blocks array.
  onPatchBlock: PropTypes.func.isRequired,
};

BlockCanvas.defaultProps = {
  selectedId: null,
};

export default BlockCanvas;
